import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BsArrowDown, BsArrowUp, BsXLg } from 'react-icons/bs';

import axiosInstance from '../../axiosInstance';
import { themeExpert } from '../../theme/expert';

const identifiant = () => Math.random().toString(36).slice(2, 9);

/** Une vignette qu'on peut saisir à la souris. */
const Vignette = memo(function Vignette({ photo, taille = 80, depuis,
                                          groupeUid, onPrise, onLachee }) {
  const [prise, setPrise] = useState(false);

  return (
    <Box
      draggable
      onDragStart={() => { setPrise(true); onPrise(photo.id, depuis, groupeUid); }}
      onDragEnd={() => { setPrise(false); onLachee(); }}
      title={photo.title || ''}
      sx={{
        opacity: prise ? 0.3 : 1,
        cursor: 'grab',
        borderRadius: 1,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: 'divider',
        flexShrink: 0,
        transition: 'opacity 0.15s',
        userSelect: 'none',
      }}
    >
      <Box component="img" src={photo.thumbnail || photo.file}
           alt={photo.title || ''} draggable={false} loading="lazy"
           sx={{ width: taille, height: taille, objectFit: 'cover',
                 display: 'block' }} />
    </Box>
  );
});

/** Un groupe de photos : son titre, son rang, et ce qu'il contient. */
const Groupe = memo(function Groupe({
  groupe, index, total, onTitre, onMonter, onDescendre, onRetirer,
  onPrise, onLachee, onDeposerDansGroupe, onDeposerAvantPhoto,
}) {
  const [survol, setSurvol] = useState(false);
  const base = 1000 + index * 100;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1,
               mb: 1.5, overflow: 'hidden' }}>
      <Stack direction="row" spacing={1} alignItems="center"
             sx={{ bgcolor: 'primary.main', color: 'primary.contrastText',
                   px: 1.5, py: 1 }}>
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap',
                                            opacity: 0.8 }}>
          Groupe {index + 1}
        </Typography>
        <TextField
          value={groupe.title}
          onChange={(e) => onTitre(e.target.value)}
          placeholder="Ex. : 20/05/2026 — cuisine, rez-de-chaussée"
          size="small"
          variant="standard"
          sx={{ flex: 1,
                '& .MuiInput-input': { color: 'primary.contrastText' },
                '& .MuiInput-root:before': { borderBottomColor: 'rgba(255,255,255,.4)' },
                '& .MuiInput-input::placeholder': { color: 'rgba(255,255,255,.6)',
                                                    opacity: 1 } }}
        />
        <Tooltip title="Monter">
          <span>
            <IconButton size="small" onClick={onMonter} disabled={index === 0}
                        sx={{ color: 'inherit' }}>
              <BsArrowUp />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Descendre">
          <span>
            <IconButton size="small" onClick={onDescendre}
                        disabled={index === total - 1}
                        sx={{ color: 'inherit' }}>
              <BsArrowDown />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Défaire le groupe">
          <IconButton size="small" onClick={onRetirer} sx={{ color: 'inherit' }}>
            <BsXLg />
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
        onDragLeave={(e) => {
          if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
            setSurvol(false);
          }
        }}
        onDrop={(e) => { setSurvol(false); onDeposerDansGroupe(e); }}
        sx={{ minHeight: 96, p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75,
              alignContent: 'flex-start', transition: 'background-color .15s',
              bgcolor: survol ? 'action.selected' : 'background.default' }}
      >
        {groupe.photos.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ m: 'auto' }}>
            Glissez des photos ici
          </Typography>
        )}

        {groupe.photos.map((photo, i) => (
          <Box key={photo.id} sx={{ position: 'relative' }}
               onDragOver={(e) => e.stopPropagation()}
               onDrop={(e) => { e.stopPropagation();
                                onDeposerAvantPhoto(e, photo.id); }}>
            <Vignette photo={photo} taille={70} depuis="groupe"
                      groupeUid={groupe.uid} onPrise={onPrise}
                      onLachee={onLachee} />
            <Box sx={{ position: 'absolute', bottom: 2, right: 2,
                       bgcolor: 'rgba(0,0,0,.6)', color: '#fff',
                       fontSize: 9, px: 0.5, borderRadius: 0.5 }}>
              {base + i}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

/**
 * Ranger les photos du dossier en groupes, à la souris.
 *
 * L'ordre est porté par `group` : le groupe n donne la centaine 1000 + n×100,
 * et la position dans le groupe l'unité. C'est cet ordre que le rapport suit.
 */
function OrganisePhotos({ show, onHide, imageFiles, onSaved }) {
  const [groupes, setGroupes] = useState([]);
  const [nonClassees, setNonClassees] = useState([]);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [survolGauche, setSurvolGauche] = useState(false);
  // La prise en cours vit dans une référence : la mettre dans l'état
  // re-rendrait tout l'arbre à chaque mouvement de souris.
  const prise = useRef(null);

  // Volontairement sur `show` seul : on fige la répartition à l'ouverture,
  // pour qu'un rafraîchissement de la liste derrière ne défasse pas le
  // rangement en cours.
  useEffect(() => {
    if (!show) return;

    const classees = imageFiles.filter((f) => f.group >= 1000);
    const reste = imageFiles.filter((f) => !(f.group >= 1000));

    const paquets = new Map();
    classees.forEach((photo) => {
      const centaine = Math.floor(photo.group / 100);
      if (!paquets.has(centaine)) {
        paquets.set(centaine, { uid: identifiant(), title: photo.title || '',
                                photos: [] });
      }
      paquets.get(centaine).photos.push({ ...photo });
    });

    setGroupes([...paquets.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, g]) => ({ ...g,
                         photos: [...g.photos].sort((a, b) => a.group - b.group) })));
    setNonClassees([...reste].sort((a, b) => a.id - b.id));
    prise.current = null;
    setSurvolGauche(false);
    setErreur('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const ajouterGroupe = () =>
    setGroupes((etat) => [...etat, { uid: identifiant(), title: '', photos: [] }]);

  const retirerGroupe = (uid) => {
    const groupe = groupes.find((g) => g.uid === uid);
    if (groupe?.photos.length) {
      setNonClassees((etat) => [...etat, ...groupe.photos]);
    }
    setGroupes((etat) => etat.filter((g) => g.uid !== uid));
  };

  const titrerGroupe = (uid, title) =>
    setGroupes((etat) => etat.map((g) => (g.uid === uid ? { ...g, title } : g)));

  const deplacerGroupe = (index, sens) => setGroupes((etat) => {
    const cible = index + sens;
    if (cible < 0 || cible >= etat.length) return etat;
    const suite = [...etat];
    [suite[index], suite[cible]] = [suite[cible], suite[index]];
    return suite;
  });

  const commencerPrise = useCallback((photoId, depuis, groupeUid) => {
    prise.current = { photoId, depuis, groupeUid };
  }, []);

  const finirPrise = useCallback(() => { prise.current = null; }, []);

  const deposerHorsGroupe = (e) => {
    e.preventDefault();
    setSurvolGauche(false);
    const encours = prise.current;
    if (!encours || encours.depuis === 'nonClassees') {
      prise.current = null;
      return;
    }

    let photo = null;
    const suite = groupes.map((g) => {
      if (g.uid !== encours.groupeUid) return g;
      photo = g.photos.find((p) => p.id === encours.photoId);
      return { ...g, photos: g.photos.filter((p) => p.id !== encours.photoId) };
    });

    if (photo) {
      setGroupes(suite);
      setNonClassees((etat) => [...etat, photo]);
    }
    prise.current = null;
  };

  // `avantPhotoId` à null : la photo va en fin de groupe.
  const deposerDansGroupe = (e, versUid, avantPhotoId = null) => {
    e.preventDefault();
    const encours = prise.current;
    if (!encours) return;

    if (encours.depuis === 'groupe' && encours.groupeUid === versUid
        && avantPhotoId === encours.photoId) {
      prise.current = null;
      return;
    }

    let photo = null;
    let resteNonClassees = nonClassees;

    if (encours.depuis === 'nonClassees') {
      photo = nonClassees.find((p) => p.id === encours.photoId);
      resteNonClassees = nonClassees.filter((p) => p.id !== encours.photoId);
    }

    let suite = groupes.map((g) => {
      if (encours.depuis === 'groupe' && g.uid === encours.groupeUid) {
        const trouvee = g.photos.find((p) => p.id === encours.photoId);
        if (trouvee) photo = trouvee;
        return { ...g,
                 photos: g.photos.filter((p) => p.id !== encours.photoId) };
      }
      return g;
    });

    if (!photo) { prise.current = null; return; }

    suite = suite.map((g) => {
      if (g.uid !== versUid) return g;
      if (avantPhotoId === null) return { ...g, photos: [...g.photos, photo] };
      const position = g.photos.findIndex((p) => p.id === avantPhotoId);
      const photos = [...g.photos];
      photos.splice(position === -1 ? photos.length : position, 0, photo);
      return { ...g, photos };
    });

    setGroupes(suite);
    setNonClassees(resteNonClassees);
    prise.current = null;
  };

  const enregistrer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const ecritures = [];

      groupes.forEach((groupe, gi) => {
        const base = 1000 + gi * 100;
        groupe.photos.forEach((photo, pi) => {
          ecritures.push({ id: photo.id, group: base + pi,
                           title: groupe.title });
        });
      });

      // Une photo sortie d'un groupe reprend le rang par défaut.
      nonClassees.forEach((photo) => {
        if (photo.group >= 1000) {
          ecritures.push({ id: photo.id, group: 0, title: '' });
        }
      });

      await Promise.all(ecritures.map((e) => {
        const corps = new FormData();
        corps.append('group', e.group);
        corps.append('title', e.title);
        return axiosInstance.patch(`/files/${e.id}/`, corps);
      }));

      onSaved();
      onHide();
    } catch {
      setErreur("Le rangement n'a pas pu être enregistré. Rien n'a changé.");
    } finally {
      setOccupe(false);
    }
  };

  const classees = groupes.reduce((somme, g) => somme + g.photos.length, 0);
  const total = classees + nonClassees.length;

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={Boolean(show)} onClose={onHide} fullWidth maxWidth="xl">
        <DialogTitle sx={{ pb: 0 }}>
          Ranger les photos
          <Typography variant="caption" color="text.secondary" component="div">
            {total} photo{total !== 1 ? 's' : ''} — l'ordre des groupes est
            celui que suivra le rapport.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                 sx={{ minHeight: '60vh' }}>
            <Box sx={{ width: { md: '34%' }, flexShrink: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Non rangées ({nonClassees.length})
              </Typography>
              <Box
                onDragOver={(e) => { e.preventDefault(); setSurvolGauche(true); }}
                onDragLeave={(e) => {
                  if (!e.relatedTarget
                      || !e.currentTarget.contains(e.relatedTarget)) {
                    setSurvolGauche(false);
                  }
                }}
                onDrop={deposerHorsGroupe}
                sx={{ minHeight: 400, maxHeight: 'calc(70vh - 60px)',
                      overflowY: 'auto', p: 1, borderRadius: 1,
                      display: 'flex', flexWrap: 'wrap', gap: 0.75,
                      alignContent: 'flex-start',
                      transition: 'background-color .15s',
                      border: '1px solid', borderColor: 'divider',
                      bgcolor: survolGauche ? 'action.selected'
                                            : 'background.default' }}
              >
                {nonClassees.length === 0 && (
                  <Typography variant="body2" color="text.secondary"
                              sx={{ m: 'auto', textAlign: 'center' }}>
                    Toutes les photos sont rangées.
                  </Typography>
                )}
                {nonClassees.map((photo) => (
                  <Vignette key={photo.id} photo={photo} depuis="nonClassees"
                            groupeUid={null} onPrise={commencerPrise}
                            onLachee={finirPrise} />
                ))}
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={2} alignItems="center"
                     justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Groupes ({groupes.length}) — {classees} rangée
                  {classees !== 1 ? 's' : ''}
                </Typography>
                <Button size="small" variant="outlined" onClick={ajouterGroupe}>
                  Nouveau groupe
                </Button>
              </Stack>

              <Box sx={{ maxHeight: 'calc(70vh - 60px)', overflowY: 'auto',
                         pr: 0.5 }}>
                {groupes.length === 0 && (
                  <Typography variant="body2" color="text.secondary"
                              sx={{ textAlign: 'center', mt: 6 }}>
                    Créez un groupe, puis glissez-y des photos.
                  </Typography>
                )}

                {groupes.map((groupe, index) => (
                  <Groupe
                    key={groupe.uid}
                    groupe={groupe}
                    index={index}
                    total={groupes.length}
                    onTitre={(titre) => titrerGroupe(groupe.uid, titre)}
                    onMonter={() => deplacerGroupe(index, -1)}
                    onDescendre={() => deplacerGroupe(index, 1)}
                    onRetirer={() => retirerGroupe(groupe.uid)}
                    onPrise={commencerPrise}
                    onLachee={finirPrise}
                    onDeposerDansGroupe={(e) => deposerDansGroupe(e, groupe.uid,
                                                                  null)}
                    onDeposerAvantPhoto={(e, avant) => deposerDansGroupe(
                      e, groupe.uid, avant)}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onHide} disabled={occupe}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer} disabled={occupe}>
            {occupe ? 'Enregistrement…'
              : `Enregistrer le rangement (${classees})`}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default OrganisePhotos;
